import api from '../../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FiInbox } from 'react-icons/fi'
import { FaFilter } from 'react-icons/fa'
import { VscClearAll } from 'react-icons/vsc'

import Input from "../../../shared/components/form/input"
import useMyPermission from '../../../hooks/useMyPermission'
import ListServiceOrderConcludedRow from './ListRow/ListServiceOrderConcludedRow'
import ServiceOrderConcludedFilterModal from './ServiceOrderConcludedFilterModal'
import '../ServiceOrder/ServiceOrder.css'

const EMPTY_FILTERS = { client: null, generator: null, technician: null, dateFrom: '', dateTo: '' }

function ServiceOrderConcluded() {

    const [token] = useState(localStorage.getItem('token') || '')
    const [serviceOrders, setServiceOrders] = useState([])
    const [search, setSearch] = useState("")
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [modalFilter, setModalFilter] = useState(false)
    const [load, setLoad] = useState(false)

    const [myPermission, setMyPermission] = useState({ group: "" })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, "serviceOrderConcluded_View")

    const getAll = () => {
        api.get(`/serviceOrder/getall`, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            if (response) {
                setServiceOrders(response.data.serviceOrders)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    useEffect(() => { getAll() }, [token])

    const concludedServiceOrders = useMemo(
        () => serviceOrders
            .filter((item) => item.status?.value === 'concluida')
            .sort((a, b) => new Date(b.reviewedAt || b.updatedAt) - new Date(a.reviewedAt || a.updatedAt)),
        [serviceOrders]
    )

    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        return concludedServiceOrders.filter((item) => {
            const matchSearch =
                (item.name || '').toLowerCase().includes(searchLowerCase) ||
                (item.code || '').toLowerCase().includes(searchLowerCase)
            const matchClient = !filters.client || item.client?._id === filters.client._id || item.client?.value === filters.client.value
            const matchGenerator = !filters.generator || item.generator?._id === filters.generator._id || item.generator?.value === filters.generator.value
            const matchTechnician = !filters.technician || (item.technicians || []).some(
                (t) => (t._id || t.value) === (filters.technician._id || filters.technician.value)
            )
            const matchDateFrom = !filters.dateFrom || (item.scheduledDate && item.scheduledDate >= filters.dateFrom)
            const matchDateTo = !filters.dateTo || (item.scheduledDate && item.scheduledDate <= filters.dateTo)
            return matchSearch && matchClient && matchGenerator && matchTechnician && matchDateFrom && matchDateTo
        })
    }, [concludedServiceOrders, search, filters])

    const hasActiveFilter = !!search
        || !!filters.client
        || !!filters.generator
        || !!filters.technician
        || !!filters.dateFrom
        || !!filters.dateTo

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando ordens de serviço...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <div className="pg-title-row">
                            <p className="pg-title">Ordem de Serviço</p>
                            <span className="pg-title-tag so-title-tag-green">Concluídas</span>
                        </div>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions" />
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar ordem de serviço..."
                                    value={search}
                                    handleOnChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'ordem' : 'ordens'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'ordem encontrada' : 'ordens encontradas'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListServiceOrderConcludedRow
                        dataArray={filter}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma ordem de serviço concluída</p>
                        <p className="pg-empty-subtitle">
                            {concludedServiceOrders.length === 0
                                ? 'As OS encerradas depois da revisão aparecem aqui.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            {modalFilter && (
                <ServiceOrderConcludedFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                    serviceOrders={concludedServiceOrders}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar ordens de serviço</p>
        </div>
    )
}

export default ServiceOrderConcluded
